import omit from 'lodash/omit';
import { type SearchUnpackedOffersParams, searchUnpackedOffers } from 'service/unpacked/resource';
import {
	searchCategories,
	type SearchCategoriesParams,
	type SearchUserCategoriesParams,
	userCategories,
} from './resource';
import type { CategoryData } from './model';
import {
	getOffersCountMap,
	makeCategoriesTree,
	offerCategoryDataToCategoryData,
	type BazarCategoryTreeCategory,
} from './utils';
import isEqual from 'lodash/isEqual';

const SORT_BY_COUNT = '-adverts_count';
export const CATEGORY_LIMIT = 2000;

export async function getSellerCategories(
	filter: Omit<SearchCategoriesParams, 'sort' | 'limit'>,
	headers?: HeadersInit,
) {
	let categories: CategoryData[] = [];

	try {
		categories = await searchCategories(
			{
				...filter,
				sort: SORT_BY_COUNT,
				limit: CATEGORY_LIMIT.toString(),
			},
			headers,
		);
	} catch (error) {
		console.error('getSellerCategories error', error);
	}

	return makeCategoriesTree(categories);
}

export async function getUserCategoriesCounts(
	availableCategories: BazarCategoryTreeCategory[],
	filter: Omit<SearchUserCategoriesParams, 'sort' | 'limit'>,
	headers?: HeadersInit,
) {
	let categories: CategoryData[] = [];

	try {
		categories = await userCategories(
			{
				...filter,
				limit: CATEGORY_LIMIT.toString(),
				offset: '0',
				sort: SORT_BY_COUNT,
			},
			headers,
		);
	} catch (error) {
		console.error('getUserCategoriesCounts error', error);
	}

	return getOffersCountMap(availableCategories, categories);
}

export async function getUserCategoriesFull(
	filter: Omit<SearchUserCategoriesParams, 'sort' | 'limit'>,
	headers?: HeadersInit,
) {
	try {
		const availableCategoriesFilters = {
			advert_status: filter.advert_status ?? 'active',
			limit: CATEGORY_LIMIT.toString(),
			offset: '0',
			sort: SORT_BY_COUNT,
		} as const;
		const categoriesCountFilters = {
			...filter,
			limit: CATEGORY_LIMIT.toString(),
			offset: '0',
			sort: SORT_BY_COUNT,
		} as const;

		const areFiltersEqual = isEqual(availableCategoriesFilters, categoriesCountFilters);

		const [availableCategories, categoriesCount] = await Promise.all([
			userCategories(availableCategoriesFilters, headers),
			areFiltersEqual ? null : userCategories(categoriesCountFilters, headers),
		]);
		const availableCategoriesTree = makeCategoriesTree(availableCategories);

		return {
			availableCategories: availableCategoriesTree,
			categoriesCounts: getOffersCountMap(availableCategoriesTree, categoriesCount ?? availableCategories),
		};
	} catch (error) {
		console.error('getUserCategoriesFull error', error);

		return null;
	}
}

export async function getSellerCategoriesCounts(
	availableCategories: BazarCategoryTreeCategory[],
	filter: Omit<SearchCategoriesParams, 'sort' | 'limit'>,
	headers?: HeadersInit,
) {
	let categories: CategoryData[] = [];

	try {
		categories = await searchCategories(
			{
				...omit(filter, 'category_id'),
				limit: CATEGORY_LIMIT.toString(),
				offset: '0',
				sort: SORT_BY_COUNT,
			},
			headers,
		);
	} catch (error) {
		console.error('getSellerCategoriesCounts error', error);
	}

	return getOffersCountMap(availableCategories, categories);
}

export async function getEshopCategoriesCounts(
	availableCategories: BazarCategoryTreeCategory[],
	filter: Omit<SearchUnpackedOffersParams, 'limit' | 'sort'>,
	headers?: HeadersInit,
) {
	let categories: CategoryData[] = [];

	try {
		categories = (
			await searchUnpackedOffers(
				{
					...omit(filter, 'category_id'),
					include_leaf_categories: true,
					offset: '0',
					limit: '1', // todo: once zbozi starts recognizing 0 as 0 and not as unset, change it to 0
				},
				headers,
			)
		).leaf_categories.map(offerCategoryDataToCategoryData);
	} catch (error) {
		console.error('getEshopCategoriesCounts error', error);
	}

	return getOffersCountMap(availableCategories, categories);
}
